import React from "react";
import useFormattedText from "helpers/useFormattedText";
import { createRoot } from 'react-dom/client';
import Teaser from "components/01-composites/teaser--card";
import Pricing from "components/01-composites/pricing--teaser";
import App from 'components/05-react/App';
import { format, isAfter } from 'date-fns';

// Import the Apollo client. See 01-base/graphql/apollo-client.js.
import client from 'kineticGraphql/apollo-client';
import * as Queries from './queries.graphql';

const getJSONfromFormattedText = (text) => {
  let json = {};
  try {
    json = JSON.parse(text.replace(/<\/?p>/g, '').trim().replace(/&quot;/g, '"'));
  } catch(err) {
    console.error(err);
  } finally {
    return json;
  }
}

// Format the teaser data.
// This has to work for events and products.
function preprocessTeaser(teaser) {
  const vars = {
    teaserImg: {
      width: '150px',
      height: 'auto',
      alt: null,
      urlPath: null
    },
    pricing: null,
    pricingDesc: null,
    eyebrow: null,
    eventFormats: [],
    designation: null,
    fieldIncludedWithSubscription: teaser.fieldIncludedWithSubscription
  };
  // Variable for end date. Need this to check if event is over.
  let eventOver = false;
  // Format the data to be displayed in the teaser.
  if (teaser.fieldActiveDatesRawField && teaser.fieldDatesRawField) {
    const { first } = teaser.fieldActiveDatesRawField;
    const startDate = new Date(first.value * 1000);
    const endDate = new Date(first.endValue * 1000);
    const eventEnd = teaser.fieldDatesRawField.first.endValue;
    eventOver = isAfter(new Date(), new Date(eventEnd * 1000));
    let formattedDate = `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')}`;
    if (startDate.getMonth() === endDate.getMonth()) {
      if (startDate.getDay() === endDate.getDay()) {
        // Same day.
        formattedDate = format(startDate, 'MMM d, yyyy');
      } else {
        // Same month different days.
        formattedDate = `${format(startDate, 'MMM d')}-${format(endDate, 'd, yyyy')}`;
      }
    }
    if (eventOver) {
      // Put Through Recording end.
      formattedDate = `Through ${format(endDate, 'MMM d, yyyy')}`;
    }
    // Check if event is over.
    vars.eyebrow = formattedDate;
  }
  const location = teaser?.fieldLocation?.locality;
  if (location) {
    vars.eyebrow += ` | ${location}`;
  }
  // PRICING.
  if (teaser?.fieldPMemberPricing?.length > 0) {
    for (let i = 0; i < teaser.fieldPMemberPricing.length; i++) {
      const pricing = teaser.fieldPMemberPricing[i];
      if (!eventOver) {
        // If event isn't over, show the first member price.
        vars.pricing = pricing.pricingInfoJson;
        break;
      } else if (pricing.fieldType === 'merchandise') {
        // Find the first member price that is merchandise.
        vars.pricing = pricing.pricingInfoJson;
        break;
      }
    }
    if (vars.pricing) {
      vars.pricing = getJSONfromFormattedText(vars.pricing);
    }
  }
  // EVENT FORMATS.
  if (teaser?.fieldEventFormats) {
    vars.eventFormats = teaser.fieldEventFormats.filter((format) => {
      let test = true;
      if (eventOver) {
        test = format.machineName === vars?.pricing?.format;
      }
      return test && format.fieldIcon;
    });
  }

  // Teaser Image can be an image or from DAM.
  if (teaser.fieldTeaserImage) {
    if ('acquiaDamAssetId' in teaser.fieldTeaserImage) {
      const { name, acquiaDamAssetId } = teaser.fieldTeaserImage;
      const vid = acquiaDamAssetId?.versionId || '';
      vars.teaserImg.urlPath = `https://americascus.widen.net/content/${vid}/web/${name}?width=600`;
      vars.teaserImg.alt = name;
    } else if ('thumbnail' in teaser.fieldTeaserImage) {
      const { alt, image } = teaser.fieldTeaserImage.thumbnail;
      vars.teaserImg.urlPath = image.urlPath;
      vars.teaserImg.alt = alt;
    }
  }

  // Designation is optional.
  vars.designation = teaser.fieldDesignation?.label;
  return vars;
}

async function fetchPromotedContent(id) {
  try {
    const promotedContentResponse = await client.query({
      query: Queries.PromotedContent,
      variables: {
        id: [ id ],
      },
    });
    return promotedContentResponse.data.entityQuery.items[0];
  }
  catch(err) {
    console.error(err);
  }
}

// Fetch the promoted content.
(async () => {
  const root = document.getElementById('event-search-root');
  const { facets, filters, title, resultQueryName, sort, dateRange, promotedId, itemsPerPage } = root.dataset;

  async function preprocessResults(results) {
    if (typeof promotedId === 'undefined') {
      return results;
    }
    // We're taking advantage of the apollo cache so this will
    // only be called once.
    const promotedContent = await fetchPromotedContent(promotedId);
    // Insert the promoted content.
    // If there are less than 3 results, add nulls to the array.
    if (results.length < 3) {
      results.push(...Array(3 - results.length).fill(null));
    }
    // Insert the promoted item at the 4th position.
    results.splice(3, 0, promotedContent);
    return results;
  }

  function renderResults(results) {
    return results.map((item, index) => {
      if (!item) {
        return <div className="spacer"></div>;
      }
      const {
        eyebrow,
        designation,
        teaserImg,
        pricing,
        eventFormats,
        fieldIncludedWithSubscription
      } = preprocessTeaser(item);
      const teaserClass = index === 3 && promotedId !== undefined ? 'promoted-event' : 'teaser-event';
      // This component has to work for both products and event_training types
      return (
        <Teaser
          containerClass={teaserClass}
          key={item.id}
          tagTheme={item?.type?.fieldColor || 'purple'}
          tag={item?.type?.label || 'Event/Training'}
          eyebrow={eyebrow}
          title={item.label}
          url={item.url.path}
        >
          <Teaser.above>
            { eventFormats.length > 0 &&
              <div className="d-flex gap-2">
                {eventFormats.map((format) => (
                  <img key={format.fieldIcon.id} src={format.fieldIcon.fieldMediaSvg.entity.uriRawField.first.url} alt={format.icon} />
                ))}
              </div>
            }
          </Teaser.above>
          <Teaser.content>
            {pricing && <Pricing price={pricing.price} earlyPrice={pricing.early_price} description={pricing.description} disclaimer={pricing.disclaimer} subscription={fieldIncludedWithSubscription} /> }
            {!pricing && item.fieldTeaserDescription &&
              <div dangerouslySetInnerHTML={useFormattedText(item.fieldTeaserDescription, 150)} />
            }
            {teaserClass === 'promoted-event' && designation &&
              <div className="d-flex gap-1 my-2 justify-content-center align-items-center">
                <span className='designation fs-xs text-uppercase'> {designation} </span>
              </div>
            }
          </Teaser.content>
          <Teaser.below>
           {teaserClass === 'teaser-event' &&
              <div className="d-flex gap-1 my-2 justify-content-center align-items-center">
                { designation && <span className='designation fs-xs text-uppercase'> {designation} </span>}
              </div>
            }
            { item.fieldTeaserImage &&
              <div className="teaser__media">
                <img loading='lazy' src={teaserImg.urlPath} width={teaserImg.width} height={teaserImg.height} alt={teaserImg.alt} />
              </div>
            }
          </Teaser.below>
        </Teaser>
      );
    });
  }

  // Render the App.
  createRoot(root).render(<App
    viewFacets={facets ? JSON.parse(facets) : undefined}
    viewTitle={title}
    viewItemsPerPage={parseInt(itemsPerPage, 10)}
    viewFilters={JSON.parse(filters)}
    viewResultQuery={Queries[resultQueryName]}
    viewSort={JSON.parse(sort)}
    viewDateRange={dateRange ? JSON.parse(dateRange) : undefined}
    viewRenderResults={renderResults}
    viewPreprocessResults={preprocessResults}
    viewContainerClass="events-search-view"
  />);

})();
